import React, { useCallback, useState } from 'react'
import { useWindowSize } from 'react-use'
import {
  List,
  Datagrid,
  TextField,
  DeleteWithConfirmButton,
  Create,
  SimpleForm,
  Edit,
  FormDataConsumer,
  SelectInput,
  ReferenceArrayInput,
  DateField,
  useNotify,
  useRedirect,
  SimpleShowLayout,
  Toolbar,
  SaveButton,
  DeleteButton,
  useRecordContext,
  ReferenceField,
  Button,
  useRefresh,
} from 'react-admin'

import {
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField as MuiTextField,
  Button as MuiButton,
} from '@mui/material'
import SendIcon from '@mui/icons-material/Send'

import { cloneDeep, set } from 'lodash'

import { yupResolver } from '@hookform/resolvers/yup'
import * as Yup from 'yup'

import { EmailEditor, EmailEditorProvider } from 'easy-email-editor'
import { StandardLayout } from 'easy-email-extensions'

import 'easy-email-editor/lib/style.css'
import 'easy-email-extensions/lib/style.css'
import '@arco-themes/react-easy-email-theme/css/arco.css'

import {
  ImportTemplateDropdown,
  ExportTemplateDropdown,
  onUploadImage,
  convertToJSON,
  convertToHTML,
  initialTemplateValues,
  defaultCategories,
  fontList,
  TemplateVariables,
  useMergeTagsModal,
  sendTest,
} from '..'

// Validation Schema
const EmailTemplateSchema = Yup.object().shape({
  name: Yup.string().required('Template name is required'),
  type: Yup.string().required('Type is required'),
})

const SendTestButton = ({ customEmail = false }) => {
  const [open, setOpen] = useState(false)
  const [email, setEmail] = useState('')
  const record = useRecordContext()
  const notify = useNotify()

  const handleClick = (event) => {
    event.stopPropagation()
    setOpen(true)
  }

  const handleClose = () => {
    setOpen(false)
  }

  const handleSend = (event) => {
    event.stopPropagation()

    const data = customEmail
      ? {
          templateName: record.name,
          email,
        }
      : {
          templateName: record.name,
        }

    sendTest('NotificationTemplate', { data })
      .then(() => {
        notify(`Test notification '${record.name}' sent successfully`, { type: 'info' })
        handleClose()
      })
      .catch((error) => {
        notify(`Error sending test notification: ${error.message}`, { type: 'warning' }) //
      })
  }

  return (
    <>
      <Button
        startIcon={<SendIcon />}
        label="Send Test"
        onClick={customEmail ? handleClick : handleSend}
        sx={{ zIndex: 9999 }}
      />
      <Dialog open={open} onClose={handleClose} onClick={(event) => event.stopPropagation()}>
        <DialogTitle>Send Test Notification</DialogTitle>
        <DialogContent>
          <MuiTextField
            autoFocus
            margin="dense"
            id="email"
            label="Email Address"
            type="email"
            fullWidth
            variant="standard"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value)
            }}
          />
        </DialogContent>
        <DialogActions>
          <MuiButton onClick={handleClose}>Cancel</MuiButton>
          <MuiButton onClick={handleSend}>Send</MuiButton>
        </DialogActions>
      </Dialog>
    </>
  )
}

export const NotificationTemplateList = ({ ...props }) => (
  <List>
    <Datagrid rowClick="edit">
      <TextField source="name" label="Template Name" />
      <TextField source="type" label="Type" />
      <ReferenceField source="createdBy" reference="users" />
      <DateField source="createdAt" label="Created At" showTime />
      <ReferenceField source="updatedBy" reference="users" />
      <DateField source="updatedAt" label="Updated At" showTime />
      <SendTestButton {...props} />
      <DeleteWithConfirmButton />
    </Datagrid>
  </List>
)

export const NotificationTemplateCreate = ({
  projectInitialTemplate = null,
  resourceName,
  ...props
}) => {
  const [emailEditorValues, setEmailEditorValues] = useState('')
  const { mergeTags, setMergeTags } = useMergeTagsModal({})

  const redirect = useRedirect()
  const notify = useNotify()
  const refresh = useRefresh()
  const { width } = useWindowSize()
  const smallScene = width < 1400

  const onChangeMergeTag = useCallback(
    (path, val) => {
      setMergeTags((old) => {
        const newObj = cloneDeep(old)
        set(newObj, path, val)
        return newObj
      })
    },
    [setMergeTags]
  )

  const transformData = async (data) => ({
    ...data,
    source: await convertToJSON(emailEditorValues),
    html: await convertToHTML(emailEditorValues, mergeTags),
  })

  const onSuccess = (data) => {
    notify(`Notification template "${data.name}" created successfully`, { type: 'info' })
    if (resourceName) redirect('list', resourceName)
    refresh()
  }

  const templateData = projectInitialTemplate?.content
    ? projectInitialTemplate
    : initialTemplateValues

  return (
    <Create transform={transformData} mutationOptions={{ onSuccess }}>
      <SimpleForm resolver={yupResolver(EmailTemplateSchema)}>
        <FormDataConsumer>
          {({ formData, ...rest }) => {
            return (
              <>
                <ReferenceArrayInput
                  source="name"
                  reference="serviceTemplates"
                  label="Template name"
                >
                  <SelectInput source="name" label="Template name" />
                </ReferenceArrayInput>

                <SelectInput
                  source="type"
                  defaultValue={'EMAIL'}
                  choices={[
                    { id: 'EMAIL', name: 'EMAIL' },
                    // { id: 'SMS', name: 'SMS' },
                  ]}
                />

                <TemplateVariables templateName={formData?.name} setMergeTags={setMergeTags} />

                <div style={{ width: '100%' }}>
                  <EmailEditorProvider
                    height={'calc(100vh - 90px)'}
                    data={templateData}
                    onUploadImage={onUploadImage}
                    fontList={fontList}
                    onChangeMergeTag={onChangeMergeTag}
                    autoComplete
                    dashed={false}
                    mergeTags={mergeTags}
                    mergeTagGenerate={(tag) => `{{${tag}}}`}
                  >
                    {({ values }, { submit, restart }) => {
                      setEmailEditorValues(values)

                      return (
                        <>
                          <Stack direction="row" spacing={1} m={2} justifyContent={'flex-end'}>
                            <ImportTemplateDropdown restart={restart} />
                            <ExportTemplateDropdown
                              values={values}
                              mergeTags={mergeTags}
                              templateName={formData?.name}
                            />
                          </Stack>

                          <StandardLayout
                            compact={!smallScene}
                            showSourceCode={true}
                            categories={defaultCategories}
                          >
                            <EmailEditor />
                          </StandardLayout>
                        </>
                      )
                    }}
                  </EmailEditorProvider>
                </div>
              </>
            )
          }}
        </FormDataConsumer>
      </SimpleForm>
    </Create>
  )
}

export const NotificationTemplateEdit = () => {
  const [emailEditorValues, setEmailEditorValues] = useState('')
  const [initialTemplateData, setInitialTemplateData] = useState({})
  const { mergeTags, setMergeTags } = useMergeTagsModal({})
  const { width } = useWindowSize()
  const smallScene = width < 1400

  const onChangeMergeTag = useCallback(
    (path, val) => {
      setMergeTags((old) => {
        const newObj = cloneDeep(old)
        set(newObj, path, val)
        return newObj
      })
    },
    [setMergeTags]
  )

  const CustomToolbar = ({ ...props }) => {
    return (
      <Toolbar {...props}>
        <Stack direction="row" justifyContent="space-between" width="100%">
          <SaveButton alwaysEnable={true} />
          <DeleteButton />
        </Stack>
      </Toolbar>
    )
  }

  const InitialDataSetter = () => {
    const record = useRecordContext()

    if (!initialTemplateData.source) {
      const parsedRecord = { ...record, source: JSON.parse(record.source) }
      setInitialTemplateData(parsedRecord)
    }
  }

  const TemplateTitle = () => {
    return <span>Template {initialTemplateData ? `"${initialTemplateData.name}"` : ''}</span>
  }

  const transformData = async (data) => ({
    ...data,
    source: await convertToJSON(emailEditorValues),
    html: await convertToHTML(emailEditorValues, mergeTags),
  })

  return (
    <Edit title={<TemplateTitle />} transform={transformData}>
      <SimpleShowLayout>
        <InitialDataSetter />
        <TextField source="name" label="Template name" />
        <TextField source="type" />
      </SimpleShowLayout>
      <SimpleForm resolver={yupResolver(EmailTemplateSchema)} toolbar={<CustomToolbar />}>
        <FormDataConsumer>
          {({ formData, ...rest }) => {
            return (
              <>
                <TemplateVariables templateName={formData?.name} setMergeTags={setMergeTags} />

                <div style={{ width: '100%' }}>
                  {initialTemplateData.source ? (
                    <EmailEditorProvider
                      height={'calc(100vh - 90px)'}
                      data={initialTemplateData.source}
                      onUploadImage={onUploadImage}
                      fontList={fontList}
                      onChangeMergeTag={onChangeMergeTag}
                      autoComplete
                      dashed={false}
                      mergeTags={mergeTags}
                      mergeTagGenerate={(tag) => `{{${tag}}}`}
                    >
                      {({ values }, { submit, restart }) => {
                        setEmailEditorValues(values)

                        return (
                          <>
                            <Stack direction="row" spacing={1} m={2} justifyContent={'flex-end'}>
                              <ImportTemplateDropdown restart={restart} />
                              <ExportTemplateDropdown
                                values={values}
                                mergeTags={mergeTags}
                                templateName={formData?.name}
                              />
                            </Stack>
                            <StandardLayout
                              compact={!smallScene}
                              showSourceCode={true}
                              categories={defaultCategories}
                            >
                              <EmailEditor />
                            </StandardLayout>
                          </>
                        )
                      }}
                    </EmailEditorProvider>
                  ) : (
                    <div>Loading template data...</div>
                  )}
                </div>
              </>
            )
          }}
        </FormDataConsumer>
      </SimpleForm>
    </Edit>
  )
}
